import React, { useEffect, useState } from 'react';
import { LeftCircleOutlined } from '@ant-design/icons';
import { ReflexContainer, ReflexElement, ReflexSplitter } from 'react-reflex';
import { Link } from 'react-router-dom';
import {
    useGetDataAvailabilityDate,
    useGetFilteredEntityCodes,
    useGetMentionableUsers,
    useGetQueryParam,
    useGetReportById,
} from 'waypoint-hooks';
import {
    EntityReport,
    EntityReportWidgetListItem,
    Report,
    EntityReportWidget,
    PropertyType,
    User,
} from 'waypoint-types';
import theme from 'config/theme';
import { SelectedEntityReportWidget } from './entity-report-widgets/EntityReportWidgetUtils';
import {
    Button,
    Progress,
    message,
    Divider,
    Tooltip,
    Empty,
    Skeleton,
} from 'antd';
import { useCommentsPane } from 'contexts/comments/CommentsContext';
import { CommentsButton } from 'waypoint-react/comment/CommentsButton';
import { CommentsBadge } from 'waypoint-react/comment/CommentsBadge';
import {
    COMPLETE,
    IN_PROGRESS,
    NarrativePositions,
    OPEN,
    PDFExportOptions,
    WidgetTypes,
} from '../constants';
import { safeDivision } from 'waypoint-utils';
import EntityReportWorkflowStatus from './EntityReportWorkflowStatus';
import { exportReportById, updateEntityReportWidget } from 'waypoint-requests';
import { DisabledDashboard, HtmlEditorContainer } from 'waypoint-react';
import { selectCurrentUser } from 'state/user/selectors';
import { connect, RootStateOrAny } from 'react-redux';
import {
    COMMENT_ID_QUERY_KEY,
    createCoverPageWidget,
    ENTITY_CODE_KEY,
    filterWidgetsWithPermissions,
    FROM_MY_REPORTS_KEY,
    REPORT_ID_KEY,
    userWorkflowRole,
    WIDGET_ID_KEY,
} from '../ReportUtils';
import { selectPropertiesById } from 'state/properties/selectors';
import { clearQueryParam } from 'waypoint-hooks/useGetQueryParam';
import EntityReportPDFExportModal from './EntityReportPDFExportModal';
import { format } from 'date-fns';
import { css } from 'emotion';
import { selectUsers } from 'state/users/selectors';
import { numericalSort } from 'utils/tables/sorters';
import saveAs from 'file-saver';
import { usePermissions } from 'contexts';

const reportNameFont = css`
    font-weight: bold;
    font-size: 26px;
    margin: 0;
`;

const propertyNameFont = css`
    font-size: 20px;
`;

const gridHeaderStyle = {
    fontWeight: 'bold',
    fontSize: '22px',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    borderTop: '1px solid lightgrey',
    borderBottom: '1px solid lightgrey',
    paddingLeft: '15px',
    paddingRight: '25px',
};

interface EntityReportEditorProps {
    userId: number;
    property: PropertyType;
    companyName: string;
    users: User[];
}

const EntityReportEditor = ({
    userId,
    property,
    companyName,
    users,
}: EntityReportEditorProps) => {
    const { featurePermissions } = usePermissions();
    const reportId = useGetQueryParam(REPORT_ID_KEY) ?? '';
    const entityCode = useGetQueryParam(ENTITY_CODE_KEY) ?? '';
    const commentIdQueryParam = useGetQueryParam(COMMENT_ID_QUERY_KEY);
    const widgetIdQueryParam = useGetQueryParam(WIDGET_ID_KEY);
    const fromMyReportParam = useGetQueryParam(FROM_MY_REPORTS_KEY) ?? 0;
    const propertyName = property?.display_name ?? '';
    const entityCodes = useGetFilteredEntityCodes();

    const [entityReport, setEntityReport] = useState<EntityReport | null>(null);

    const [selectedEntityReportWidget, setSelectedEntityReportWidget] =
        useState<EntityReportWidgetListItem | null>(null);
    const [reportWidgetList, setReportWidgetList] = useState<
        EntityReportWidgetListItem[]
    >([]);
    const [showInstructions, setShowInstructions] = useState<boolean>(false);
    const [pdfExportModalIsOpen, setPdfExportModalIsOpen] =
        useState<boolean>(false);
    const [selectedExportOption, setSelectedExportOption] = useState<string>(
        PDFExportOptions.ENTIRE_REPORT
    );
    const [selectedExportComponentIds, setSelectedExportComponentIds] =
        useState<string[]>([]);
    const [widgetExportList, setWidgetExportList] = useState<
        EntityReportWidgetListItem[]
    >([]);

    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [showAccessError, setShowAccessError] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const {
        update: updateCommentsPane,
        initializeComments,
        openComments,
        closeComments,
    } = useCommentsPane();

    const { data: report, isValidating } = useGetReportById(reportId);
    const { data: asOfDate } = useGetDataAvailabilityDate([entityCode]);

    const userMentionOptions = useGetMentionableUsers([entityCode]);

    useEffect(() => {
        updateCommentsPane({
            userMentionOptions,
        });
    }, [userMentionOptions]);

    useEffect(() => {
        if (
            entityCodes.filteredEntityCodes.length &&
            !entityCodes.filteredEntityCodes.includes(entityCode)
        ) {
            setShowAccessError(true);
            return;
        }

        if (commentIdQueryParam) {
            const reportWidget = reportWidgetList.find(
                (widget) => widget.id === widgetIdQueryParam
            );
            if (reportWidget) {
                setSelectedEntityReportWidget(reportWidget);
                initializeComments(reportWidget.comment_thread_id);
                openComments();
                updateCommentsPane({
                    heading: report?.name ?? '',
                    subheading: `${propertyName} - ${reportWidget.name}`,
                    scrollToCommentId: commentIdQueryParam,
                });
            }
        }
    }, [commentIdQueryParam, selectedEntityReportWidget]);

    const [isNarrativeTextEditorOpen, setIsNarrativeTextEditorOpen] =
        useState<boolean>(false);

    const coverPageSettings = report?.reportSettings.find(
        (setting) => setting.settings_type === WidgetTypes.CoverPage
    );

    const setEntityReportAndWidgetList = (data: Report) => {
        const entityReportByEntityCode =
            data.entityReports.find(
                (entityReport) => entityReport.entity_code === entityCode
            ) ?? null;

        setEntityReport(entityReportByEntityCode ?? null);

        const widgetList = [
            ...data.reportWidgets
                .filter((reportWidget) =>
                    filterWidgetsWithPermissions(
                        featurePermissions,
                        reportWidget
                    )
                )
                .map((reportWidget) => {
                    const entityReportWidget =
                        entityReportByEntityCode?.entityReportWidgets?.find(
                            (widget) =>
                                widget.report_widget_id === reportWidget.id
                        );
                    return {
                        ...reportWidget,
                        entityReportWidgetId: entityReportWidget?.id ?? '',
                        narrative_text:
                            entityReportWidget?.narrative_text ?? '',
                        comment_thread_id:
                            entityReportWidget?.reportMetadata
                                .comment_thread_id ?? '',
                        reportMetadata: entityReportWidget?.reportMetadata,
                        status: entityReportWidget?.status,
                    };
                }),
        ].sort((a, b) => numericalSort(a.sort_index, b.sort_index));

        if (coverPageSettings?.enabled) {
            const coverPageWidget = createCoverPageWidget(
                coverPageSettings?.settings?.paperOrientation,
                coverPageSettings?.settings?.paperSize
            );
            widgetList.unshift(coverPageWidget);
        }

        setReportWidgetList(widgetList);
        setSelectedEntityReportWidget(widgetList[0]);
        updateCommentsPane({
            heading: data.name,
            subheading: `${propertyName} - ${widgetList[0].name}`,
        });
    };
    useEffect(() => {
        if (!report) {
            return;
        }

        setEntityReportAndWidgetList(report);
    }, [report]);

    useEffect(() => {
        if (selectedExportOption === PDFExportOptions.CURRENT_SECTION) {
            setWidgetExportList([
                selectedEntityReportWidget ?? reportWidgetList[0],
            ]);
            return;
        }
        setWidgetExportList(
            reportWidgetList.filter((w) =>
                selectedExportComponentIds.includes(w.id)
            )
        );
    }, [selectedExportOption, selectedExportComponentIds]);

    const resetExportModalSettings = () => {
        setWidgetExportList([]);
        setSelectedExportComponentIds([]);
        setSelectedExportOption(PDFExportOptions.ENTIRE_REPORT);
    };

    const onCancelPdfExportModal = () => {
        resetExportModalSettings();
        setPdfExportModalIsOpen(false);
    };

    const exportDocument = async () => {
        try {
            setIsExporting(true);

            const widgetsToExport =
                selectedExportOption === PDFExportOptions.ENTIRE_REPORT
                    ? reportWidgetList
                    : widgetExportList;

            const widgetIndices = widgetsToExport.map((w) => w.sort_index);
            const download = await exportReportById({
                reportId: reportId,
                entityCode,
                widgetIndices,
            });

            const formattedDate = format(new Date(), 'yyyy-MM-dd');
            const blob = await download.blob();
            saveAs(
                blob,
                `${propertyName}_${
                    report?.name ?? 'report'
                }_${formattedDate}.pdf`
            );

            setPdfExportModalIsOpen(false);
            resetExportModalSettings();
        } catch (e) {
            console.error(e);
            message.error('Download Failed');
        } finally {
            setIsExporting(false);
        }
    };

    const onStatusChanged = (status: string | null) => {
        if (!status) {
            return;
        }

        const updatedReportWidgetList = reportWidgetList
            .map((widget) => {
                if (widget.id === selectedEntityReportWidget?.id) {
                    return {
                        ...widget,
                        status: status,
                    };
                }
                return widget;
            })
            .filter((reportWidget) =>
                filterWidgetsWithPermissions(featurePermissions, reportWidget)
            );

        selectedEntityReportWidget &&
            setSelectedEntityReportWidget({
                ...selectedEntityReportWidget,
                status: status,
            });

        setReportWidgetList(updatedReportWidgetList);
    };

    const calculatedProgress = () => {
        const widgetsWithoutCoverPage = reportWidgetList.filter(
            (widget) => widget.widget_type !== WidgetTypes.CoverPage
        );

        const complete = widgetsWithoutCoverPage.filter(
            (item) => item.status === COMPLETE
        ).length;
        const success =
            safeDivision(complete, widgetsWithoutCoverPage.length) * 100;
        const inProgress = widgetsWithoutCoverPage.filter(
            (item) => item.status === IN_PROGRESS
        ).length;
        const percentage =
            safeDivision(
                complete + inProgress,
                widgetsWithoutCoverPage.length
            ) * 100;
        return {
            success: Number(success.toFixed(1)),
            percentage: Number(percentage.toFixed(1)),
        };
    };

    const toggleInstructions = () => {
        setShowInstructions(!showInstructions);
    };

    const onClickSelectedWidget = (widget: EntityReportWidgetListItem) => {
        if (widget.id !== selectedEntityReportWidget?.id) {
            setSelectedEntityReportWidget(widget);
            setShowInstructions(false);

            clearQueryParam(COMMENT_ID_QUERY_KEY);
            clearQueryParam(WIDGET_ID_KEY);
        }
    };

    const WIDGET_LIST_HEIGHT = window.innerHeight - 275;

    const widgetHeight = window.innerHeight - 214;

    const styleNarrative = {
        border: isNarrativeTextEditorOpen ? 'none' : '1px dashed lightgrey',
        borderRadius: 5,
        padding: isNarrativeTextEditorOpen ? 0 : 16,
    };

    const narrativeTextBox = () => (
        <div
            style={{ marginTop: 12 }}
            key={selectedEntityReportWidget?.id ?? ''}
        >
            <HtmlEditorContainer
                disabled={!userWorkflowRole({ userId, entityReport })}
                style={
                    selectedEntityReportWidget?.narrative_position === 'above'
                        ? {
                              ...styleNarrative,
                              position: 'relative',
                              marginBottom: 16,
                          }
                        : {
                              ...styleNarrative,
                              position: 'relative',
                              marginTop: 16,
                          }
                }
                height={300}
                items={{
                    notes: selectedEntityReportWidget?.narrative_text,
                }}
                title={'narrative'}
                onHtmlEditorSubmit={(value) =>
                    selectedEntityReportWidget &&
                    updateNarrativeTexts({
                        ...selectedEntityReportWidget,
                        narrative_text: value.notes,
                    })
                }
                onEditCallback={() =>
                    setIsNarrativeTextEditorOpen(!isNarrativeTextEditorOpen)
                }
            />
        </div>
    );

    const updateNarrativeTexts = async (
        widget: EntityReportWidget | EntityReportWidgetListItem
    ) => {
        const updatedEntityReportWidgetList = reportWidgetList
            .map((reportWidget) => {
                if (reportWidget.id === widget.id) {
                    return {
                        ...reportWidget,
                        narrative_text: widget.narrative_text,
                    };
                }
                return reportWidget;
            })
            .filter((reportWidget) =>
                filterWidgetsWithPermissions(featurePermissions, reportWidget)
            );
        setReportWidgetList(updatedEntityReportWidgetList);
        const updatedEntityReportWidget = updatedEntityReportWidgetList.find(
            (w) => w.id === widget.id
        );
        try {
            await updateEntityReportWidget(
                updatedEntityReportWidget?.entityReportWidgetId ?? '',
                updatedEntityReportWidget?.narrative_text ?? '',
                [entityCode]
            );
            updatedEntityReportWidget &&
                setSelectedEntityReportWidget(updatedEntityReportWidget);
            message.success('Narrative updated successfully');
        } catch (e) {
            message.error('Error updating narrative');
        }
    };

    const reportNavItem = (
        title: string,
        iconName: string,
        status: string,
        widget: EntityReportWidgetListItem,
        color: string
    ) => {
        if (widget.status !== status) {
            return null;
        }

        return (
            <Tooltip title={title} placement={'right'} arrow>
                <i
                    className={iconName}
                    style={{
                        color,
                        marginRight: 5,
                    }}
                />
            </Tooltip>
        );
    };

    const isCoverPageSelected =
        selectedEntityReportWidget?.widget_type === WidgetTypes.CoverPage;
    const isNarrativeBelow =
        selectedEntityReportWidget?.narrative_position ===
        NarrativePositions.Below;
    const isNarrativeAbove =
        selectedEntityReportWidget?.narrative_position ===
        NarrativePositions.Above;

    const getWidgetStyles = (
        widget: EntityReportWidgetListItem,
        selectedEntityReportWidget: EntityReportWidgetListItem | null
    ) => {
        const baseStyles = {
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            backgroundColor:
                widget.id === selectedEntityReportWidget?.id
                    ? theme.colors.blues.focusRow
                    : '',
        };

        const coverPageStyles = {
            borderBottom: '1px solid lightgrey',
            marginLeft: '-10px',
            padding: '10px',
            fontWeight: 'bold',
        };

        const otherWidgetStyles = {
            marginBottom: '5px',
            marginRight: '10px',
            padding: '5px',
        };

        return widget.id === WidgetTypes.CoverPage
            ? { ...baseStyles, ...coverPageStyles }
            : { ...baseStyles, ...otherWidgetStyles };
    };

    if (!report || !entityReport || !propertyName || !asOfDate) {
        return <DisabledDashboard text={'Loading'} />;
    }

    if (showAccessError) {
        return (
            <Empty
                style={{ marginTop: '150px' }}
                description="You do not have access to this report."
            />
        );
    }

    return (
        <>
            {!isValidating && (
                <EntityReportPDFExportModal
                    key={pdfExportModalIsOpen.toString()}
                    isModalOpen={pdfExportModalIsOpen}
                    isExporting={isExporting}
                    exportDocument={exportDocument}
                    reportWidgetList={reportWidgetList}
                    selectedEntityReportWidgetName={
                        selectedEntityReportWidget?.name ?? ''
                    }
                    selectedExportOption={selectedExportOption}
                    setSelectedExportOption={setSelectedExportOption}
                    selectedExportComponentIds={selectedExportComponentIds}
                    setSelectedExportComponentIds={
                        setSelectedExportComponentIds
                    }
                    onCancel={onCancelPdfExportModal}
                />
            )}
            <ReflexContainer orientation="horizontal">
                <ReflexElement>
                    <ReflexContainer orientation="vertical">
                        <ReflexElement size={400} minSize={200} maxSize={700}>
                            <div
                                style={{
                                    height: '75px',
                                    paddingTop: '15px',
                                    paddingLeft: '20px',
                                }}
                            >
                                {fromMyReportParam ? (
                                    <Link to="/reports">
                                        <LeftCircleOutlined
                                            style={{ marginRight: 5 }}
                                        />
                                        Back to My Reports
                                    </Link>
                                ) : (
                                    <Link to={`/report/manager/${reportId}`}>
                                        <LeftCircleOutlined
                                            style={{ marginRight: 5 }}
                                        />
                                        Back to Report
                                    </Link>
                                )}
                            </div>
                            <div>
                                <div style={gridHeaderStyle}>
                                    Report Sections
                                </div>

                                {!isValidating && (
                                    <div
                                        style={{
                                            marginTop: '2px',
                                            paddingLeft: '10px',
                                            paddingBottom: '20px',
                                            height: WIDGET_LIST_HEIGHT,
                                            overflowY: 'auto',
                                        }}
                                    >
                                        {reportWidgetList.map((widget) => {
                                            return (
                                                <div
                                                    onClick={() => {
                                                        onClickSelectedWidget(
                                                            widget
                                                        );
                                                        setIsNarrativeTextEditorOpen(
                                                            false
                                                        );
                                                        initializeComments(
                                                            widget.comment_thread_id
                                                        );
                                                        updateCommentsPane({
                                                            heading:
                                                                report.name,
                                                            subheading: `${propertyName} - ${widget.name}`,
                                                        });
                                                        closeComments();
                                                    }}
                                                    style={getWidgetStyles(
                                                        widget,
                                                        selectedEntityReportWidget
                                                    )}
                                                >
                                                    {reportNavItem(
                                                        'Complete',
                                                        'fa-solid fa-check-circle',
                                                        COMPLETE,
                                                        widget,
                                                        theme.colors
                                                            .workflowReportStatus
                                                            .approved
                                                    )}
                                                    {reportNavItem(
                                                        'In Progress',
                                                        'fa-solid fa-pencil-square',
                                                        IN_PROGRESS,
                                                        widget,
                                                        theme.colors
                                                            .workflowReportStatus
                                                            .in_progress
                                                    )}
                                                    {reportNavItem(
                                                        'Open',
                                                        'fa-solid fa-ban',
                                                        OPEN,
                                                        widget,
                                                        theme.colors
                                                            .workflowReportStatus
                                                            .open
                                                    )}
                                                    <span
                                                        style={{
                                                            flex: 1,
                                                            textOverflow:
                                                                'ellipsis',
                                                            overflow: 'hidden',
                                                            whiteSpace: 'pre',
                                                        }}
                                                    >
                                                        {widget.name}
                                                    </span>
                                                    <span
                                                        style={{
                                                            marginLeft: 'auto',
                                                        }}
                                                    >
                                                        <CommentsBadge
                                                            commentThreadId={
                                                                widget.comment_thread_id
                                                            }
                                                            onClicked={() => {
                                                                onClickSelectedWidget(
                                                                    widget
                                                                );
                                                                updateCommentsPane(
                                                                    {
                                                                        heading:
                                                                            report.name,
                                                                        subheading: `${propertyName} - ${widget.name}`,
                                                                    }
                                                                );
                                                            }}
                                                            badgeStyle={{
                                                                marginLeft:
                                                                    '10px',
                                                            }}
                                                            hideZeroCount={true}
                                                        />
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {isValidating && (
                                    <div style={{ padding: 15 }}>
                                        <Skeleton paragraph={{ rows: 10 }} />
                                    </div>
                                )}
                                <div
                                    style={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        padding: '10px 25px 25px 25px',
                                        borderTop: '2px solid lightgrey',
                                    }}
                                >
                                    Status
                                    <Progress
                                        percent={
                                            calculatedProgress().percentage
                                        }
                                        success={{
                                            percent:
                                                calculatedProgress().success,
                                        }}
                                        format={(percent, successPercentage) =>
                                            `${successPercentage}%`
                                        }
                                    />
                                </div>
                            </div>
                        </ReflexElement>
                        <ReflexSplitter />
                        <ReflexElement>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignContent: 'center',
                                    height: '75px',
                                }}
                            >
                                <div></div>
                                <div
                                    style={{
                                        justifyContent: 'center',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        flexGrow: '1',
                                    }}
                                >
                                    <div className={reportNameFont}>
                                        {propertyName}{' '}
                                    </div>
                                    <div className={propertyNameFont}>
                                        {report?.name}{' '}
                                    </div>
                                </div>
                                <div
                                    style={{
                                        top: 10,
                                        right: 25,
                                        position: 'absolute',
                                        zIndex: 1,
                                    }}
                                >
                                    <Button
                                        disabled={isUploading}
                                        style={{
                                            color: theme.colors.white,
                                            backgroundColor:
                                                theme.colors.oranges.bright,
                                            border: theme.colors.grays.medium,
                                        }}
                                        onClick={() =>
                                            setPdfExportModalIsOpen(true)
                                        }
                                    >
                                        Export to PDF
                                    </Button>
                                </div>
                            </div>

                            {!isValidating && (
                                <div>
                                    <div style={gridHeaderStyle}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            <div>
                                                {selectedEntityReportWidget?.name ??
                                                    ''}
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'flex-start',
                                                marginLeft: '30px',
                                            }}
                                        >
                                            <EntityReportWorkflowStatus
                                                entityReportWidget={
                                                    selectedEntityReportWidget
                                                }
                                                entityReportWorkflowRows={
                                                    entityReport?.workflowRole ??
                                                    []
                                                }
                                                onStatusChanged={
                                                    onStatusChanged
                                                }
                                            />
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                marginTop: '3px',
                                                marginLeft: '30px',
                                            }}
                                        >
                                            {selectedEntityReportWidget?.widget_type !==
                                                WidgetTypes.CoverPage && (
                                                <CommentsButton
                                                    onClicked={() => {
                                                        initializeComments(
                                                            selectedEntityReportWidget?.comment_thread_id ??
                                                                ''
                                                        );
                                                        updateCommentsPane({
                                                            heading:
                                                                report.name,
                                                            subheading: `${propertyName} - ${selectedEntityReportWidget?.name}`,
                                                        });
                                                    }}
                                                    commentThreadId={
                                                        selectedEntityReportWidget?.comment_thread_id
                                                    }
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div
                                style={{
                                    marginTop: '10px',
                                    marginLeft: '10px',
                                    marginRight: '10px',
                                    height: `${widgetHeight}px`,
                                    overflowY:
                                        selectedEntityReportWidget?.widget_type ===
                                        'attachment'
                                            ? 'hidden'
                                            : 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                {isValidating && (
                                    <Skeleton paragraph={{ rows: 6 }} />
                                )}
                                {!isValidating && (
                                    <>
                                        <div
                                            style={{
                                                display: 'inline-block',
                                                fontSize: '14px',
                                                fontWeight: 'normal',
                                            }}
                                        >
                                            {selectedEntityReportWidget?.instructions &&
                                                selectedEntityReportWidget
                                                    .instructions.length && (
                                                    <Button
                                                        style={{
                                                            padding: '0',
                                                            marginLeft: '3px',
                                                        }}
                                                        type="link"
                                                        onClick={
                                                            toggleInstructions
                                                        }
                                                    >
                                                        <p
                                                            style={{
                                                                textDecoration:
                                                                    'underline',
                                                            }}
                                                        >
                                                            {!showInstructions
                                                                ? 'Show Instructions'
                                                                : 'Hide Instructions'}
                                                        </p>
                                                    </Button>
                                                )}
                                        </div>
                                        {selectedEntityReportWidget?.instructions &&
                                            selectedEntityReportWidget
                                                .instructions.length &&
                                            showInstructions && (
                                                <div
                                                    style={{
                                                        fontSize: '14px',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            fontSize: '13px',
                                                            margin: '10px',
                                                            marginLeft: '5px',
                                                            marginRight: '5px',
                                                            display: 'flex',
                                                            flexDirection:
                                                                'column',
                                                            borderRadius: '5px',
                                                            padding: '10px',

                                                            boxShadow:
                                                                'rgba(151, 151, 160, 0.63) 0px 0px 0px 2px',

                                                            backgroundColor:
                                                                'lightyellow',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                fontSize:
                                                                    '14px',
                                                                fontWeight:
                                                                    'bold',
                                                                textDecoration:
                                                                    'underline',
                                                                marginBottom:
                                                                    '5px',
                                                            }}
                                                        >
                                                            Instructions:
                                                        </div>
                                                        {
                                                            selectedEntityReportWidget?.instructions
                                                        }
                                                    </div>
                                                    <Divider
                                                        style={{
                                                            margin: '15px auto',
                                                        }}
                                                    />
                                                </div>
                                            )}

                                        {!isCoverPageSelected &&
                                        isNarrativeAbove
                                            ? narrativeTextBox()
                                            : null}
                                        <div
                                            style={{
                                                marginTop: '10px',
                                                height: isCoverPageSelected
                                                    ? '100%'
                                                    : 'auto',
                                            }}
                                        >
                                            <SelectedEntityReportWidget
                                                key={
                                                    selectedEntityReportWidget?.entityReportWidgetId ??
                                                    ''
                                                }
                                                asOfDate={asOfDate}
                                                entityCode={entityCode}
                                                widgetId={
                                                    selectedEntityReportWidget?.entityReportWidgetId ??
                                                    ''
                                                }
                                                widgetType={
                                                    selectedEntityReportWidget?.widget_type ??
                                                    ''
                                                }
                                                widgetSettings={
                                                    selectedEntityReportWidget?.settings_inputs ??
                                                    {}
                                                }
                                                setIsUploading={setIsUploading}
                                                isUploading={isUploading}
                                                propertyInfo={property}
                                                entityReport={entityReport}
                                                users={users}
                                                report={report}
                                            />
                                        </div>
                                    </>
                                )}
                                {!isCoverPageSelected && isNarrativeBelow
                                    ? narrativeTextBox()
                                    : null}
                            </div>
                        </ReflexElement>
                    </ReflexContainer>
                </ReflexElement>
            </ReflexContainer>
        </>
    );
};

const mapState = (s: RootStateOrAny) => {
    const { id: userId, display_name: companyName } = selectCurrentUser(s);
    const users = selectUsers(s);

    const entityCode = new URLSearchParams(
        s.routing.locationBeforeTransitions.search
    ).get(ENTITY_CODE_KEY);
    const properties = selectPropertiesById(s, { propertyIds: [entityCode] });
    const property = Object.values(properties);
    return {
        users,
        userId,
        companyName,
        property: property[0] as PropertyType,
    };
};

export default connect(mapState)(EntityReportEditor);
